import type {
	I2VAspectRatio,
	I2VModelId,
	I2VResolution,
} from "@saas/image-to-video/types";

/**
 * /bulin-47-ai-video-generator 的预设：一张照片（人或宠物）换进 Bulin 47 和 Kevin Wai 那段街头
 * freestyle（2026-10-15）。方案见 docs/research/bulin-47-ai-research-2026-10-15.md。
 * 这里只放 type import，apps/web/test 的 transpile loader 才能直接加载。
 */
export const BULIN_47_PAGE_PATH = "/bulin-47-ai-video-generator";

/** 登录前现场的 IndexedDB key，和 /image-to-video、首页、其他预设页分开 */
export const BULIN_47_PENDING_STATE_KEY = "pendingBulin47State";

/**
 * 第二槽的默认场景图（gpt-image-2 生成的红灯餐厅门口 + 背对镜头举手机和音箱的卷发男，中间空着）。
 * Lite（Grok）靠它出场景、两张图才认 9:16；Standard / HD 跟原片走，prompt 里只当氛围参考
 */
export const BULIN_47_SCENE_PATH = "home/showcase/bulin-47/refs/scene.jpg";

/**
 * Standard / HD 的参考视频：原片 10 秒（media.io 那份 1080p 原片的 3.4–13.4s，从 "ya tú me conoces
 * y Bulin 47" 起到 "Hágale mi hermano… Capotillo" 收），缩成 720×1280 24fps、去音轨，YuNet + SFace
 * 只给 Bulin 打码。不打码 Mini 不换人（实测 p1），打码后 Mini / H3 人和宠物都能换。
 * 放在用户上传那个桶 images-art-prod：后端只收 NEXT_PUBLIC_R2_PUBLIC_URL 下的参考视频。
 * 服务端认这个 key，出片后把音轨换成同一段原声（packages/api 的 preset-audio.ts）
 */
export const BULIN_47_MOTION_PATH = "presets/bulin-47/motion-masked-v1.mp4";
export const BULIN_47_MOTION_SECONDS = 10;
export const BULIN_47_MOTION_ASPECT_RATIO: I2VAspectRatio = "9:16";

export type Bulin47QualityId = "lite" | "standard" | "hd";

/**
 * 2026-10-15 老公定（10 秒窗口）：Lite 走 Grok 吃免费额度（6 秒 14 积分）；Standard 走 Seedance 2.0 Mini
 * 480p、HD 走 MiniMax H3 720p（上游 768P），都带 10 秒打码原片，按（10 + 10）秒计 48 / 160 积分，
 * HD 正好一个 $6.90 入门包。Lite 和 Standard 换了模型，hero 按模型 + 分辨率认档才不撞
 */
export const BULIN_47_QUALITIES: {
	id: Bulin47QualityId;
	model: I2VModelId;
	resolution: I2VResolution;
	duration: number;
}[] = [
	{ id: "lite", model: "fast-3.0", resolution: "480p", duration: 6 },
	{
		id: "standard",
		model: "seedance-2-mini",
		resolution: "480p",
		duration: 10,
	},
	{ id: "hd", model: "quality-2.0-ref", resolution: "720p", duration: 10 },
];

export const BULIN_47_PRESET = {
	mode: "reference",
	defaultQualityId: "lite" as Bulin47QualityId,
	/** 原片是竖的，三档都只出 9:16 */
	aspectRatioOptions: ["9:16"] as I2VAspectRatio[],
	defaultAspectRatio: "9:16" as I2VAspectRatio,
	durationOptions: [6, 10],
	defaultDuration: 6,
	/** 主角 + 场景两个槽位，顺序即上传给模型的图片顺序 */
	slotCount: 2,
} as const;

export type Bulin47TemplateId = "me" | "pet";

/** 同其他热梗页：用户只能加一句 director's note，拼到模板后面 */
export const BULIN_47_NOTE_PREFIX = "\n\nDirector's note: ";

/** 只看 note 是否全空白，不 trim 内容本身：hero 每敲一个字都会走这里 */
export function buildBulin47Prompt(
	templatePrompt: string,
	note: string,
): string {
	return note.trim()
		? `${templatePrompt}${BULIN_47_NOTE_PREFIX}${note}`
		: templatePrompt;
}

/**
 * Standard / HD（Mini、H3 共用）：2026-10-15 直连 kie 实测 p7 / p10 的写法。kie 里图和视频分开编号，
 * 写 @Video 1 / @Image 1；不写「head to toe … exactly the outfit … pink shirt are gone」的话 Mini 只换脸、
 * 粉衬衫金链纹身都留着（p5）。站内 Seedance / H3 提示词上限 1000 字，留 ≥ 150 给 note
 */
const ME_PROMPT = `Character replacement. @Video 1 is the source performance; the lead rapper is the man whose face is pixelated, behind the curly-haired man holding the phone and portable speaker. Replace the whole lead, head to toe, with the person from @Image 1 from the first frame: exact face, hair, skin tone and body, wearing exactly the outfit from @Image 1, same garments and colors, in every frame. The lead's pink mesh shirt, chain, watch and tattoos are gone. Never show blur or pixelation. The new lead copies the original's position, gestures, head turns, mouth movements and rap timing exactly. Keep everything else identical to @Video 1: speaker holder, crowd, red-lit entrance, lighting, framing, handheld camera motion and timing. No extra people, text or watermark.`;

/**
 * 宠物单写一套：只说「换成这只动物」时 Mini 把狗加在 Bulin 前面、人还在（p9）；
 * 写明「Remove that man completely」后狗站到主角位置（p11，H3 p12 同样通过）。实测原文后面跟着
 * "no clothes, no human"，站内 Waffo 判 undress_transform 拦掉（2026-10-15），改成 "never a person"
 */
const PET_PROMPT =
	"Character replacement. @Video 1 is the source performance. The lead rapper is the man whose face is pixelated, in an open light-pink shirt, behind the curly-haired man holding the phone and portable speaker. Remove that man completely: from the first frame to the last, the animal from @Image 1 stands upright in his exact spot instead, same species, face, fur color and markings, real paws and a real animal body, never a person. The animal bobs its head, opens and closes its mouth to the rap and waves a front paw at the same moments as the original lead. Keep the speaker holder, crowd, red-lit entrance, lighting, framing and handheld camera motion from @Video 1. Never show blur or pixelation. No text or watermark.";

/** Lite（Grok 1.5，6 秒，不收视频）：照片 + 场景图重新生成一段相似的现场（实测 p13 / p14） */
const ME_LITE_PROMPT =
	"6-second handheld phone video at night, photoreal. PHOTO 2 is the scene: a red-lit restaurant entrance with red stairs, a crowd and bouncers; in the foreground a curly-haired man in a maroon hoodie, seen from behind, holds up a phone and a black portable speaker. PHOTO 1 is the lead rapper: keep their exact face, hair, body and outfit. The lead steps into the open spot in front of the speaker holder and freestyles a dembow rap straight at him, mouth moving with every word, hands chopping the beat, head nodding, grinning between lines; the crowd smiles and films. Keep the speaker holder, crowd and red entrance from PHOTO 2. Dembow beat with rap vocals. No text, logos or watermark.";

const PET_LITE_PROMPT =
	"6-second handheld phone video at night, photoreal. PHOTO 2 is the scene: a red-lit restaurant entrance with red stairs, a crowd and bouncers; in the foreground a curly-haired man in a maroon hoodie, seen from behind, holds up a phone and a black portable speaker. PHOTO 1 is the lead rapper: the exact same animal from PHOTO 1, same species, face, fur and markings, a real animal with real paws, never a human body. It stands upright on its hind legs in the open spot in front of the speaker holder and raps at him, mouth opening and closing, one front paw waving to the beat, head bobbing; the crowd laughs and films. Keep the speaker holder, crowd and red entrance from PHOTO 2. Dembow beat with rap vocals. No text, logos or watermark.";

/**
 * 两个模板都固定挂打码原片（Grok 不收视频，Lite 自动不带）；出片后服务端统一换成原声，
 * 所以模板里的配乐写什么都会被替换。test/bulin-47-preset.test.mjs 卡住长度和这几条
 */
export const BULIN_47_TEMPLATES: {
	id: Bulin47TemplateId;
	prompt: string;
	promptByQuality: Partial<Record<Bulin47QualityId, string>>;
}[] = [
	{ id: "me", prompt: ME_PROMPT, promptByQuality: { lite: ME_LITE_PROMPT } },
	{
		id: "pet",
		prompt: PET_PROMPT,
		promptByQuality: { lite: PET_LITE_PROMPT },
	},
];
